import { useEffect, useId, useRef, type ButtonHTMLAttributes, type HTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from "react";
import "./components.css";

export type Tone = "neutral" | "accent" | "success" | "warning" | "danger";

function classes(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

function Icon({ name }: { name: ReactNode }) {
  return <span className="ds-icon" aria-hidden="true">{typeof name === "string" ? <i className={name} /> : name}</span>;
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "quiet" | "danger";
  size?: "default" | "compact";
  loading?: boolean;
  icon?: ReactNode;
}

export function Button({ variant = "secondary", size = "default", loading = false, icon, disabled, className, children, type = "button", ...props }: ButtonProps) {
  return (
    <button {...props} type={type} disabled={disabled || loading} aria-busy={loading || props["aria-busy"]} className={classes("ds-button", "ds-focusable", `ds-button--${variant}`, `ds-button--${size}`, className)}>
      {loading ? <Icon name="fa-solid fa-circle-notch ds-spinner" /> : icon && <Icon name={icon} />}
      {children && <span className="ds-button-label">{children}</span>}
      {loading && <span className="ds-sr-only">Loading</span>}
    </button>
  );
}

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title?: ReactNode;
  headingId?: string;
  description?: ReactNode;
  actions?: ReactNode;
}

export function Card({ title, headingId, description, actions, className, children, ...props }: CardProps) {
  return (
    <div {...props} className={classes("ds-card", className)}>
      {(title || description || actions) && (
        <div className="ds-card-header">
          <div className="ds-card-heading">
            {title && <h2 id={headingId} className="ds-card-title">{title}</h2>}
            {description && <p className="ds-card-description">{description}</p>}
          </div>
          {actions && <div className="ds-card-actions">{actions}</div>}
        </div>
      )}
      <div className="ds-card-content">{children}</div>
    </div>
  );
}

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
}

export function Field({ label, helper, error, id, className, required, ...props }: FieldProps) {
  const generatedId = useId();
  const inputId = id || `ds-field-${generatedId}`;
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;
  const describedBy = [props["aria-describedby"], helper && helperId, error && errorId].filter(Boolean).join(" ") || undefined;
  return (
    <div className={classes("ds-field", !!error && "ds-field--error", props.disabled && "ds-field--disabled")}>
      <label className="ds-field-label" htmlFor={inputId}>{label}{required && <span className="ds-field-required"> (required)</span>}</label>
      <input {...props} id={inputId} required={required} aria-invalid={error ? true : props["aria-invalid"]} aria-describedby={describedBy} className={classes("ds-field-input", "ds-focusable", className)} />
      {helper && <p id={helperId} className="ds-field-helper">{helper}</p>}
      {error && <p id={errorId} className="ds-field-error" role="alert"><Icon name="fa-solid fa-circle-exclamation" />{error}</p>}
    </div>
  );
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
}

export function Select({ label, helper, error, id, className, required, children, ...props }: SelectProps) {
  const generatedId = useId();
  const inputId = id || `ds-select-${generatedId}`;
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;
  const describedBy = [props["aria-describedby"], helper && helperId, error && errorId].filter(Boolean).join(" ") || undefined;
  const isListBox = props.multiple || (props.size ?? 0) > 1;
  return (
    <div className={classes("ds-field", !!error && "ds-field--error", props.disabled && "ds-field--disabled")}>
      <label className="ds-field-label" htmlFor={inputId}>{label}{required && <span className="ds-field-required"> (required)</span>}</label>
      <div className="ds-select-control">
        <select {...props} id={inputId} required={required} aria-invalid={error ? true : props["aria-invalid"]} aria-describedby={describedBy} className={classes("ds-field-input", "ds-select", isListBox && "ds-select--list", "ds-focusable", className)}>{children}</select>
        {!isListBox && <span className="ds-select-icon" aria-hidden="true"><Icon name="fa-solid fa-chevron-down" /></span>}
      </div>
      {helper && <p id={helperId} className="ds-field-helper">{helper}</p>}
      {error && <p id={errorId} className="ds-field-error" role="alert"><Icon name="fa-solid fa-circle-exclamation" />{error}</p>}
    </div>
  );
}

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}

export function Dialog({ open, onClose, title, description, children, actions }: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const programmaticCloseRef = useRef(false);
  const titleId = `ds-dialog-${useId()}`;
  const descriptionId = `${titleId}-description`;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      dialog.showModal();
    } else if (!open && dialog.open) {
      programmaticCloseRef.current = true;
      dialog.close();
      if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus();
    }
  }, [open]);

  useEffect(() => () => {
    const dialog = dialogRef.current;
    if (dialog?.open) {
      programmaticCloseRef.current = true;
      dialog.close();
    }
    if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus();
  }, []);

  return (
    <dialog ref={dialogRef} className="ds-dialog" aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined} onCancel={(event) => { event.preventDefault(); onClose(); }} onClose={() => {
      if (programmaticCloseRef.current) { programmaticCloseRef.current = false; return; }
      if (open) onClose();
      if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus();
    }}>
      <div className="ds-dialog-header">
        <div className="ds-dialog-heading"><h2 id={titleId} className="ds-dialog-title">{title}</h2>{description && <p id={descriptionId} className="ds-dialog-description">{description}</p>}</div>
        <Button variant="quiet" size="compact" icon="fa-solid fa-xmark" aria-label="Close dialog" onClick={onClose} />
      </div>
      <div className="ds-dialog-content">{children}</div>
      {actions && <div className="ds-dialog-actions">{actions}</div>}
    </dialog>
  );
}

export interface MenuProps {
  label?: string;
  ariaLabel?: string;
  items: ReadonlyArray<{ label: string; onSelect: () => void; disabled?: boolean; danger?: boolean }>;
}

export function Menu({ label = "More", ariaLabel, items }: MenuProps) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const dismiss = () => {
    const details = detailsRef.current;
    if (!details?.open) return;
    details.open = false;
    details.querySelector("summary")?.focus();
  };

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const details = detailsRef.current;
      if (!details?.open || !(event.target instanceof Node) || details.contains(event.target)) return;
      details.open = false;
      details.querySelector("summary")?.focus();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  return (
    <details ref={detailsRef} className="ds-menu" onToggle={(event) => {
      const details = event.currentTarget;
      if (!details.open) return;
      const trigger = details.querySelector("summary");
      const popup = details.querySelector<HTMLElement>(".ds-menu-popup");
      if (!trigger || !popup) return;
      const triggerRect = trigger.getBoundingClientRect();
      const popupWidth = popup.getBoundingClientRect().width;
      const viewportLeft = Math.max(16, Math.min(triggerRect.right - popupWidth, window.innerWidth - popupWidth - 16));
      popup.style.left = `${viewportLeft - triggerRect.left}px`;
      popup.style.right = "auto";
    }} onKeyDown={(event) => { if (event.key !== "Escape") return; event.preventDefault(); event.stopPropagation(); dismiss(); }}>
      <summary className="ds-menu-trigger ds-focusable" aria-label={ariaLabel}>{label}<Icon name="fa-solid fa-ellipsis" /></summary>
      <div className="ds-menu-popup" role="group" aria-label={`${label} actions`}>
        {items.map((item, index) => <button key={`${item.label}-${index}`} type="button" disabled={item.disabled} className={classes("ds-menu-item", "ds-focusable", item.danger && "ds-menu-item--danger")} onClick={() => { dismiss(); item.onSelect(); }}>{item.label}</button>)}
      </div>
    </details>
  );
}

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
}

export function Badge({ tone = "neutral", className, children, ...props }: BadgeProps) {
  return <span {...props} className={classes("ds-badge", `ds-tone--${tone}`, className)}>{children}</span>;
}

export interface ProgressProps extends HTMLAttributes<HTMLSpanElement> {
  value: number;
  label?: ReactNode;
  valueLabel?: ReactNode;
  tone?: Tone;
}

export function Progress({ value, label, valueLabel, tone = "accent", className, "aria-label": ariaLabel, ...props }: ProgressProps) {
  const labelId = `ds-progress-${useId()}`;
  const percent = Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;
  return (
    <span {...props} className={classes("ds-progress", `ds-tone--${tone}`, className)}>
      {(label || valueLabel) && <span className="ds-progress-labels">{label && <span id={labelId}>{label}</span>}{valueLabel && <span className="ds-progress-value">{valueLabel}</span>}</span>}
      <span className="ds-progress-track" role="progressbar" aria-label={label ? undefined : ariaLabel || "Progress"} aria-labelledby={label ? labelId : undefined} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(percent * 10) / 10} aria-valuetext={typeof valueLabel === "string" ? valueLabel : undefined}>
        <span className="ds-progress-fill" style={{ width: `${percent}%` }} />
      </span>
    </span>
  );
}

export interface TabsProps {
  items: ReadonlyArray<{ id: string; label: ReactNode }>;
  value: string;
  onChange: (id: string) => void;
  label?: string;
  className?: string;
  panelId?: string;
}

export function Tabs({ items, value, onChange, label = "Sections", className, panelId }: TabsProps) {
  const tabsId = `ds-tabs-${useId()}`;
  return (
    <div className={classes("ds-tabs", className)} role={panelId ? "tablist" : "group"} aria-label={label}>
      {items.map((item, index) => (
        <button key={item.id} id={panelId ? `${tabsId}-${item.id}` : undefined} type="button" className="ds-tab ds-focusable" role={panelId ? "tab" : undefined} aria-selected={panelId ? value === item.id : undefined} aria-pressed={panelId ? undefined : value === item.id} aria-controls={panelId} tabIndex={value === item.id || (!items.some((tab) => tab.id === value) && index === 0) ? 0 : -1} onClick={() => onChange(item.id)} onKeyDown={(event) => {
          let nextIndex: number;
          if (event.key === "ArrowRight") nextIndex = (index + 1) % items.length;
          else if (event.key === "ArrowLeft") nextIndex = (index + items.length - 1) % items.length;
          else if (event.key === "Home") nextIndex = 0;
          else if (event.key === "End") nextIndex = items.length - 1;
          else return;
          event.preventDefault();
          const next = items[nextIndex];
          if (!next) return;
          onChange(next.id);
          event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(".ds-tab")[nextIndex]?.focus();
        }}>{item.label}</button>
      ))}
    </div>
  );
}

export interface QuestItemProps {
  title: string;
  goal: string;
  domain: string;
  kind: string;
  xp: number;
  done?: boolean;
  onComplete?: () => void;
  onOpen?: () => void;
  onPin?: () => void;
  onFocus?: () => void;
  onMore?: () => void;
  className?: string;
}

export function QuestItem({ title, goal, domain, kind, xp, done = false, onComplete, onOpen, onPin, onFocus, onMore, className }: QuestItemProps) {
  return (
    <article className={classes("ds-quest", done && "ds-quest--done", className)}>
      {onComplete ? (
        <button type="button" className="ds-quest-complete ds-focusable" onClick={onComplete} aria-label={`${done ? "Reopen" : "Complete"} ${title}`} aria-pressed={done}><span className="ds-quest-check"><Icon name="fa-solid fa-check" /></span></button>
      ) : <span className="ds-quest-complete"><span className="ds-quest-check" aria-hidden="true"><Icon name="fa-solid fa-check" /></span><span className="ds-sr-only">{done ? "Completed" : "Open"}</span></span>}
      <div className="ds-quest-heading">
        <h3 className="ds-quest-title">{onOpen ? <button type="button" className="ds-quest-open ds-focusable" onClick={onOpen}>{title}</button> : title}</h3>
        <Badge>{kind}</Badge>
      </div>
      <div className="ds-quest-metadata"><span><Icon name="fa-solid fa-bullseye" />{goal}</span><span><Icon name="fa-solid fa-layer-group" />{domain}</span></div>
      <div className="ds-quest-footer">
        <div className="ds-quest-actions">
          {onPin && <Button size="compact" icon="fa-solid fa-thumbtack" onClick={onPin} aria-label={`Pin ${title}`} title="Pin quest" />}
          {onFocus && <Button size="compact" icon="fa-solid fa-stopwatch" onClick={onFocus} aria-label={`Start focus on ${title}`} title="Start focus" />}
          {onMore && <Button size="compact" icon="fa-solid fa-ellipsis" onClick={onMore} aria-label={`More actions for ${title}`}>More</Button>}
        </div>
        <span className="ds-quest-xp">+{xp} XP</span>
      </div>
    </article>
  );
}

export interface GoalCardProps extends Pick<HTMLAttributes<HTMLElement>, "className" | "style"> {
  title: string;
  completed?: number;
  total?: number;
  progress?: number;
  label?: ReactNode;
  valueLabel?: ReactNode;
  icon?: ReactNode;
  tone?: Tone;
  onClick?: () => void;
}

export function GoalCard({ title, completed = 0, total = 0, progress, label, valueLabel, icon, tone = "accent", onClick, className, style }: GoalCardProps) {
  const calculated = total > 0 && Number.isFinite(completed) && Number.isFinite(total) ? (completed / total) * 100 : 0;
  const raw = progress ?? calculated;
  const percent = Number.isFinite(raw) ? Math.min(100, Math.max(0, raw)) : 0;
  const contents = <><span className="ds-goal-heading">{icon && <span className="ds-goal-icon" aria-hidden="true">{icon}</span>}<span className="ds-goal-title">{title}</span></span><Progress value={percent} label={label ?? `${completed} / ${total} quests`} valueLabel={valueLabel ?? `${Math.round(percent)}%`} tone={tone} /></>;
  const goalClass = classes("ds-goal", onClick && "ds-focusable", `ds-tone--${tone}`, className);
  return onClick ? <button type="button" className={goalClass} style={style} onClick={onClick}>{contents}</button> : <div className={goalClass} style={style}>{contents}</div>;
}

export interface StatProps {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
  tone?: Tone;
}

export function Stat({ label, value, detail, tone = "neutral" }: StatProps) {
  return <dl className={classes("ds-stat", `ds-tone--${tone}`)}><dt className="ds-stat-label">{label}</dt><dd className="ds-stat-value">{value}</dd>{detail && <dd className="ds-stat-detail">{detail}</dd>}</dl>;
}
